import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { authService, UserProfile, AuthTokens } from '../services/auth.service';
import { queryClient } from '../lib/query-client';
import { queryKeys } from '../lib/query-keys';

interface AuthContextType {
  user: UserProfile | null;
  tokens: AuthTokens | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  orgId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function seedProfileCache(profile: UserProfile) {
  queryClient.setQueryData(queryKeys.auth.profile(), profile);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      setTokens(null);
      queryClient.clear();
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      if (authService.isAuthenticated()) {
        try {
          const userProfile = await authService.getUserProfile();
          setUser(userProfile);
          seedProfileCache(userProfile);
          setTokens({
            access_token: authService.getAccessToken() || '',
            refresh_token: '',
            token_type: 'Bearer',
            expires_in: 0,
            refresh_expires_in: 0,
          });
        } catch (profileError) {
          console.error('Failed to fetch user profile:', profileError);
          await authService.logout();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial auth check on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Re-hydrate when tokens are stored externally (e.g. set-password flow)
  useEffect(() => {
    const handler = () => checkAuth();
    window.addEventListener('auth:tokens-stored', handler);
    return () => window.removeEventListener('auth:tokens-stored', handler);
  }, [checkAuth]);

  const login = async (username: string, password: string) => {
    setLoading(true);
    try {
      const result = await authService.login({ username, password });
      setUser(result.user);
      setTokens(result.tokens);
      seedProfileCache(result.user);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
      setUser(null);
      setTokens(null);
      queryClient.clear();
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    tokens,
    loading,
    login,
    logout,
    isAuthenticated: !!user && !!tokens,
    orgId: (user as any)?.organizationId ?? null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
